import type { ChatCompletionContext, ReviewMode } from '../types'

const senseReviewMode = (context: ChatCompletionContext): ReviewMode | null => {
  const hasCurrentDocumentContext =
    context.current_document_context && context.current_document_context.length > 0
  const hasMouseSelectionContext = context.current_document_context?.some(
    (ctx) => ctx.type === 'mouse_selection'
  )
  const hasTableHtmlContext = context.current_document_context?.some(
    (ctx) => ctx.type === 'table_html'
  )
  const hasFullDocumentContext = context.current_document_context?.some(
    (ctx) => ctx.type === 'full_document' || ctx.type === 'full_document_paragraph'
  )
  const hasSectionContext = context.current_document_context?.some((ctx) => ctx.type === 'section')

  /**
   * ReviewMode enum representing the payload we need, what we review:
   * no context from document, just reply to comments = "comments"
   * paragraph that it's attached to + comments = "paragraph_comments"
   * full document per paragraph + comments = "full_document_comments"
   * deprecated QC: mouse_selection (update + comments) = "mouse_selection_comments"
   *
   * This also drives where insertReviewComments looks for the anchor paragraph: every mode
   * except mouse_selection_comments resolves document-wide; mouse_selection_comments
   * is scoped to only the live mouse-selection paragraph ids.
   */

  if (!hasCurrentDocumentContext) {
    return 'comments'
  }
  if (hasMouseSelectionContext) {
    return 'mouse_selection_comments'
  }
  if (hasFullDocumentContext) {
    return 'full_document_comments'
  }
  if (hasSectionContext) {
    return 'paragraph_comments'
  }
  // table_html carries no record of its source, so "a mouse selection confined to one table"
  // and "a full-document/section review confined to tables" look identical here.
  // full_document_comments resolves document-wide so it can never end up with an empty search scope.
  if (hasTableHtmlContext) {
    return 'full_document_comments'
  }
  return null
}

export { senseReviewMode }
